'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

type Language = 'pt' | 'en';

type Translations = Record<string, string>;

const ptTranslations: Translations = {
  // Login
  'login.welcome': 'Bem-vindo de volta',
  'login.subtitle': 'Acesso centralizado às soluções empresariais',
  'login.emailLabel': 'Email',
  'login.emailPlaceholder': 'o-seu@email.com',
  'login.passwordLabel': 'Password',
  'login.passwordPlaceholder': '••••••••',
  'login.hidePassword': 'Esconder password',
  'login.showPassword': 'Mostrar password',
  'login.loggingIn': 'A entrar...',
  'login.loginButton': 'Entrar no HUB',
  'login.error': 'Email ou Password incorretos',

  // Hub
  'hub.userManagementBtn': 'Utilizadores',
  'hub.activeSystems': 'sistema(s) activo(s)',
  'hub.activeSystem': 'sistema activo',
  'hub.logoutBtn': 'Sair',
  'hub.heroBadge': 'SISTEMA OPERACIONAL',
  'hub.heroTitle1': 'Portfólio de',
  'hub.heroTitle2': 'Soluções',
  'hub.heroDesc': 'Acesso centralizado às plataformas empresariais Techfusion',
  'hub.syncing': 'A sincronizar sistemas...',
  'hub.services': 'serviço(s)',
  'hub.service': 'serviço',
  'hub.accessBtn': 'Acessar Software',
  
  // Badges / Status
  'status.running': 'Online',
  'status.stopped': 'Offline',
  'status.error': 'Erro',
  'status.unknown': 'N/D',
  'status.done': 'Online',

  // Users Page
  'users.backToHub': 'Voltar ao Hub',
  'users.heroBadge': 'ADMINISTRAÇÃO',
  'users.heroTitle1': 'Gestão de',
  'users.heroTitle2': 'Utilizadores',
  'users.heroDesc': 'Cria, edita e controla o acesso de cada utilizador às plataformas',
  'users.usersCount': 'utilizador(es)',
  'users.userCount': 'utilizador',
  'users.newUserBtn': 'Novo Utilizador',
  'users.loadingUsers': 'A carregar utilizadores...',
  'users.tableColUser': 'Utilizador',
  'users.tableColRole': 'Função',
  'users.tableColAccess': 'Acessos',
  'users.tableColActions': 'Ações',
  'users.roleAdmin': 'Admin',
  'users.roleUser': 'Utilizador',
  'users.accessAll': 'Todos',
  'users.softwares': 'software(s)',
  'users.software': 'software',
  'users.actionEdit': 'Editar',
  'users.actionConfirm': 'Confirmar',
  'users.actionCancel': 'Cancelar',
  'users.actionDelete': 'Eliminar',

  // Users Modal
  'modal.editTitle': 'Editar Utilizador',
  'modal.createTitle': 'Novo Utilizador',
  'modal.nameLabel': 'Nome (opcional)',
  'modal.namePlaceholder': 'Ex: João Silva',
  'modal.passwordLabel': 'Password',
  'modal.newPasswordLabel': 'Nova Password (deixar vazio para manter)',
  'modal.passwordPlaceholder': 'Password segura',
  'modal.roleTitle': 'Função',
  'modal.roleDesc': 'Admins têm acesso a todos os softwares e à gestão de utilizadores',
  'modal.roleAdminLabel': 'Administrador',
  'modal.roleUserLabel': 'Utilizador comum',
  'modal.accessLabel': 'Acesso aos Softwares',
  'modal.accessEmpty': 'Nenhum software selecionado',
  'modal.accessSearch': 'Pesquisar software...',
  'modal.cancelBtn': 'Cancelar',
  'modal.saveLoading': 'A guardar...',
  'modal.saveChanges': 'Guardar alterações',
  'modal.createUser': 'Criar utilizador',

  // DB Descriptions
  'desc.geopark': 'Site da Geopark',
  'desc.infrafusion': 'Gestão de infraestruturas e monitorização de rede avançada.',

  // Dynamic Backend Strings
  'dyn.createdUnavailable': 'Data de criação indisponível',
  'dyn.createdToday': 'Criado hoje',
  'dyn.createdDays': 'Criado há {n} dias',
  'dyn.createdMonth': 'Criado há 1 mês',
  'dyn.createdMonths': 'Criado há {n} meses',
  'dyn.createdYear': 'Criado há 1 ano',
  'dyn.createdYears': 'Criado há {n} anos',
  'dyn.serviceRunning': '1 serviço a correr no Dokploy',
  'dyn.servicesRunning': '{n} serviços a correr no Dokploy',

  // API Errors
  'error.unauthorized': 'Não autorizado',
  'error.fetchProjects': 'Erro ao obter os projetos',
  'error.update': 'Erro ao actualizar.',
  'error.create': 'Erro ao criar.',
  'error.delete': 'Erro ao eliminar.',
  'error.emailRequired': 'Email e password são obrigatórios.',
  'error.unknown': 'Erro desconhecido',
};

const enTranslations: Translations = {
  // Login
  'login.welcome': 'Welcome back',
  'login.subtitle': 'Centralized access to enterprise solutions',
  'login.emailLabel': 'Email',
  'login.emailPlaceholder': 'your@email.com',
  'login.passwordLabel': 'Password',
  'login.passwordPlaceholder': '••••••••',
  'login.hidePassword': 'Hide password',
  'login.showPassword': 'Show password',
  'login.loggingIn': 'Logging in...',
  'login.loginButton': 'Login to HUB',
  'login.error': 'Incorrect Email or Password',

  // Hub
  'hub.userManagementBtn': 'Users',
  'hub.activeSystems': 'active systems',
  'hub.activeSystem': 'active system',
  'hub.logoutBtn': 'Logout',
  'hub.heroBadge': 'SYSTEM RUNNING',
  'hub.heroTitle1': 'Portfolio of',
  'hub.heroTitle2': 'Solutions',
  'hub.heroDesc': 'Centralized access to Techfusion enterprise platforms',
  'hub.syncing': 'Syncing systems...',
  'hub.services': 'services',
  'hub.service': 'service',
  'hub.accessBtn': 'Access Software',

  // Badges / Status
  'status.running': 'Online',
  'status.stopped': 'Offline',
  'status.error': 'Error',
  'status.unknown': 'N/A',
  'status.done': 'Online',

  // Users Page
  'users.backToHub': 'Back to Hub',
  'users.heroBadge': 'ADMINISTRATION',
  'users.heroTitle1': 'User',
  'users.heroTitle2': 'Management',
  'users.heroDesc': 'Create, edit, and control user access to platforms',
  'users.usersCount': 'users',
  'users.userCount': 'user',
  'users.newUserBtn': 'New User',
  'users.loadingUsers': 'Loading users...',
  'users.tableColUser': 'User',
  'users.tableColRole': 'Role',
  'users.tableColAccess': 'Access',
  'users.tableColActions': 'Actions',
  'users.roleAdmin': 'Admin',
  'users.roleUser': 'User',
  'users.accessAll': 'All',
  'users.softwares': 'softwares',
  'users.software': 'software',
  'users.actionEdit': 'Edit',
  'users.actionConfirm': 'Confirm',
  'users.actionCancel': 'Cancel',
  'users.actionDelete': 'Delete',

  // Users Modal
  'modal.editTitle': 'Edit User',
  'modal.createTitle': 'New User',
  'modal.nameLabel': 'Name (optional)',
  'modal.namePlaceholder': 'e.g., John Doe',
  'modal.passwordLabel': 'Password',
  'modal.newPasswordLabel': 'New Password (leave empty to keep)',
  'modal.passwordPlaceholder': 'Secure password',
  'modal.roleTitle': 'Role',
  'modal.roleDesc': 'Admins have access to all software and user management',
  'modal.roleAdminLabel': 'Administrator',
  'modal.roleUserLabel': 'Standard user',
  'modal.accessLabel': 'Software Access',
  'modal.accessEmpty': 'No software selected',
  'modal.accessSearch': 'Search software...',
  'modal.cancelBtn': 'Cancel',
  'modal.saveLoading': 'Saving...',
  'modal.saveChanges': 'Save changes',
  'modal.createUser': 'Create user',

  // DB Descriptions
  'desc.geopark': 'Geopark Website',
  'desc.infrafusion': 'Infrastructure management and advanced network monitoring.',

  // Dynamic Backend Strings
  'dyn.createdUnavailable': 'Created date unavailable',
  'dyn.createdToday': 'Created today',
  'dyn.createdDays': 'Created {n} days ago',
  'dyn.createdMonth': 'Created 1 month ago',
  'dyn.createdMonths': 'Created {n} months ago',
  'dyn.createdYear': 'Created 1 year ago',
  'dyn.createdYears': 'Created {n} years ago',
  'dyn.serviceRunning': '1 service running on Dokploy',
  'dyn.servicesRunning': '{n} services running on Dokploy',

  // API Errors
  'error.unauthorized': 'Unauthorized',
  'error.fetchProjects': 'Error fetching projects',
  'error.update': 'Error updating.',
  'error.create': 'Error creating.',
  'error.delete': 'Error deleting.',
  'error.emailRequired': 'Email and password are required.',
  'error.unknown': 'Unknown error',
};

const translations = {
  pt: ptTranslations,
  en: enTranslations,
};

type LanguageContextType = {
  language: Language;
  toggleLanguage: () => void;
  changeLanguage: (lang: Language) => void;
  t: (key: string, replacements?: Record<string, string | number>) => string;
  translateDynamic: (text: string) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguage] = useState<Language>('pt');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('language') as Language;
    if (stored === 'pt' || stored === 'en') {
      setLanguage(stored);
    }
    setMounted(true);
  }, []);

  const toggleLanguage = () => {
    setLanguage((prev) => {
      const newLang = prev === 'pt' ? 'en' : 'pt';
      localStorage.setItem('language', newLang);
      return newLang;
    });
  };

  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string, replacements?: Record<string, string | number>) => {
    let str = translations[language][key] || key;
    if (replacements) {
      for (const [k, v] of Object.entries(replacements)) {
        str = str.replace(new RegExp(`{${k}}`, 'g'), String(v));
      }
    }
    return str;
  };

  const translateDynamic = (text: string) => {
    if (!text) return text;
    
    // Check known DB descriptions (exact match)
    if (text === 'Site da Geopark') return t('desc.geopark');
    if (text === 'Gestão de infraestruturas e monitorização de rede avançada.') return t('desc.infrafusion');

    // Check dynamic backend strings
    if (text === 'Created date unavailable') return t('dyn.createdUnavailable');
    if (text === 'Created today') return t('dyn.createdToday');
    if (text === '1 service running on Dokploy') return t('dyn.serviceRunning');
    
    const daysMatch = text.match(/^Created (\d+) days ago$/);
    if (daysMatch) return t('dyn.createdDays', { n: daysMatch[1] });
    
    const monthMatch = text.match(/^Created (\d+) month(s)? ago$/);
    if (monthMatch) {
      return monthMatch[1] === '1' ? t('dyn.createdMonth') : t('dyn.createdMonths', { n: monthMatch[1] });
    }
    
    const yearMatch = text.match(/^Created (\d+) year(s)? ago$/);
    if (yearMatch) {
      return yearMatch[1] === '1' ? t('dyn.createdYear') : t('dyn.createdYears', { n: yearMatch[1] });
    }

    const servicesMatch = text.match(/^(\d+) services running on Dokploy$/);
    if (servicesMatch) return t('dyn.servicesRunning', { n: servicesMatch[1] });

    return text;
  };

  // Prevent hydration mismatch by rendering children without context first,
  // or we can just render the provider and let children handle hydration.
  // Given we are mostly CSR (App.tsx is 'use client' and mounted in Next app dir but acting as SPA),
  // we can just render the context immediately.
  
  if (!mounted) {
    return null; // or a loader
  }

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, changeLanguage, t, translateDynamic }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
